import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { apiErrorMessage } from '@/shared/api-client';
import { useCreateDiscountCode } from './api';
import {
  DiscountCodeFormFields,
  discountCodeDraftInput,
  emptyDiscountCodeDraft,
  type DiscountCodeDraft,
} from './DiscountCodeForm';

export function CreateDiscountCodePage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const canManage = useAuthorization().can('discount.manage');
  const createDiscountCode = useCreateDiscountCode();

  const [draft, setDraft] = useState<DiscountCodeDraft>(emptyDiscountCodeDraft);

  if (!canManage) return <Forbidden />;

  function updateDraft(patch: Partial<DiscountCodeDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await createDiscountCode.mutateAsync(discountCodeDraftInput(draft));
      toast.success(t('discounts.toast.created'));
      navigate('/admin/discounts');
    } catch (caught) {
      toast.error(apiErrorMessage(t, 'discounts', caught));
    }
  }

  return (
    <section className="mx-auto grid max-w-2xl gap-6 p-6">
      <div>
        <button
          type="button"
          onClick={() => navigate('/admin/discounts')}
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('discounts.actions.back')}
        </button>
        <h1 className="text-2xl font-semibold">{t('discounts.create.title')}</h1>
      </div>

      <form onSubmit={submit} className="grid gap-5">
        <DiscountCodeFormFields draft={draft} onChange={updateDraft} />

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={createDiscountCode.isPending}>
            {t('discounts.actions.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/discounts')}>
            {t('discounts.actions.cancel')}
          </Button>
        </div>
      </form>
    </section>
  );
}
